/**
 * Bunny CDN image URL helpers.
 *
 * All image assets (reward-icons, profile-avatars) are pre-rendered at upload time
 * into 4 WebP variants — 64 / 128 / 256 / 512 px @ quality 85 — and served from a
 * Bunny Storage Zone via Pull Zone under a custom domain.
 *
 * DB fields (`rewards.icon_file_path`, `profile_avatars.file_path`) store only a
 * relative base path after migration (e.g. `rewards/<id>/<ts>`). The final asset
 * URL is `{VITE_BUNNY_CDN_URL}/{basePath}-{size}.webp`.
 *
 * Legacy rows may still hold full `http(s)://…` URLs (pre-migration data).
 * `getCdnImageUrl` passes those through unchanged as a safety net.
 */

export type CdnSize = 64 | 128 | 256 | 512

export const CDN_SIZES: readonly CdnSize[] = [64, 128, 256, 512] as const

const CDN_BASE = import.meta.env.VITE_BUNNY_CDN_URL

/**
 * Build-time platform flag. Backed by the `__IS_WEB__` Vite-define
 * constant set in `vite.web.config.ts` (`true`) and
 * `electron.vite.config.ts` (`false`). Vite inlines the literal at
 * compile time, so the surrounding `if` blocks dead-code-eliminate
 * per build target.
 *
 * Used to short-circuit the offline image-cache path on Web — there
 * the browser's HTTP cache + the Bunny Pull-Zone's Cache-Control
 * headers handle re-renders far more efficiently than base64 in
 * IndexedDB. We deliberately do NOT read `import.meta.env.VITE_APP_MODE`
 * because that depends on a runtime env-var being set in the build
 * environment (Vercel, CI) — easy to misconfigure. The Vite-define
 * literal is compile-time and impossible to forget.
 */
export const IS_WEB_BUILD: boolean =
  typeof __IS_WEB__ !== 'undefined' && __IS_WEB__

/**
 * Builds a Bunny-CDN URL for a pre-generated image variant.
 *
 * @param basePath Relative base path from the DB (e.g. `rewards/<id>/<ts>`).
 *                 Legacy `http(s)://…` URLs are returned unchanged.
 * @param size One of the 4 pre-rendered sizes.
 * @returns The full CDN URL, or `null` if `basePath` is empty / CDN base URL is unset.
 */
export function getCdnImageUrl(
  basePath: string | null | undefined,
  size: CdnSize
): string | null {
  if (!basePath) return null
  if (basePath.startsWith('http://') || basePath.startsWith('https://')) {
    return basePath
  }
  if (!CDN_BASE) {
    console.warn('[cdn] VITE_BUNNY_CDN_URL not set — image will not render')
    return null
  }
  return `${CDN_BASE.replace(/\/$/, '')}/${basePath}-${size}.webp`
}

/**
 * Picks the smallest pre-generated variant that is ≥ `pixelsNeeded`.
 * Pass the MAXIMUM retina pixel size this image will ever be rendered at
 * (i.e. `displaySize * devicePixelRatio` or `displaySize * 2` as a safe default).
 *
 * @example pickCdnSize(48)  // → 64  (sidebar avatar at 32px display × 2)
 * @example pickCdnSize(142) // → 256 (profile picture at 71px display × 2)
 * @example pickCdnSize(600) // → 512 (clamped — no bigger variant exists)
 */
export function pickCdnSize(pixelsNeeded: number): CdnSize {
  for (const s of CDN_SIZES) {
    if (s >= pixelsNeeded) return s
  }
  return 512
}
